import { Button as PrimeButton } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import Swal from "sweetalert2";
import { complementaryService } from "../../service/complementary.service";
import PageHeader from "../PageHeader";

type Complementary = {
  id?: number;
  name?: string;
  description?: string;
  isActive?: boolean;
};

const initialComplementary: Partial<Complementary> = {
  name: "",
  description: "",
  isActive: true,
};

const ComplementaryComponent: React.FC = () => {
  const [complementaries, setComplementaries] = useState<Complementary[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [complementary, setComplementary] =
    useState<Partial<Complementary>>(initialComplementary);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [page, setPage] = useState(() => {
    const savedPage = localStorage.getItem("complementaryTablePage");
    return savedPage ? parseInt(savedPage) : 1;
  });
  const [rows, setRows] = useState(() => {
    const savedRows = localStorage.getItem("complementaryTableRows");
    return savedRows ? parseInt(savedRows) : 10;
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sortField, setSortField] = useState<string>(() => {
    const savedSort = localStorage.getItem("complementaryTableSortField");
    return savedSort || "name";
  });
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1>(() => {
    const savedOrder = localStorage.getItem("complementaryTableSortOrder");
    return savedOrder ? (parseInt(savedOrder) as 1 | 0 | -1) : 1;
  });
  useEffect(() => {
    const fetchComplementaries = async () => {
      setListLoading(true);
      try {
        const result = await complementaryService.getAllPaginated({
          pageNumber: page,
          pageSize: rows,
          sortField,
          sortOrder: sortOrder === -1 ? "desc" : "asc",
          globalFilter,
        });

        setComplementaries(
          (result?.items ?? []).map((item: any) => ({
            ...item,
            id: typeof item.id === "string" ? parseInt(item.id, 10) : item.id,
          }))
        );
        setTotalRecords(result?.totalCount ?? 0);
      } catch {
        setComplementaries([]);
        setTotalRecords(0);
      } finally {
        setListLoading(false);
      }
    };
    fetchComplementaries();
  }, [page, rows, sortField, sortOrder, globalFilter, success, refreshKey]);

  const handleRefresh = () => setRefreshKey((k) => k + 1);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setComplementary({ ...complementary, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      if (complementary.id) {
        const updatedComplementary = await complementaryService.update(
          complementary.id,
          complementary as Complementary
        );
        setComplementaries((prev) =>
          prev.map((c) =>
            c.id === complementary.id ? updatedComplementary : c
          )
        );
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Complementary updated successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } else {
        const createdComplementary = await complementaryService.create(
          complementary as Complementary
        );
        setComplementaries((prev) => [...prev, createdComplementary]);
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Complementary created successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
      setSuccess(true);
      setComplementary(initialComplementary);
      setShowCreateForm(false);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to save complementary"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (rowData: Complementary) => {
    setComplementary(rowData);
    setShowCreateForm(true);
  };

  const handleDelete = async (rowData: Complementary) => {
    if (!rowData.id) return;
    try {
      await complementaryService.delete(rowData.id);
      // Update the local state immediately
      setComplementaries((prev) => prev.filter((c) => c.id !== rowData.id));
      setSuccess(true);
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "success",
        title: "Complementary deleted successfully.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
    } catch {
      setError("Failed to delete complementary");
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: "Failed to delete complementary.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
    }
  };

  const actionBodyTemplate = (rowData: Complementary) => (
    <div className="d-flex align-items-center gap-2">
      <Button
        variant="link"
        className="p-0"
        onClick={() => handleEdit(rowData)}
        title="Edit"
        style={{ color: "#198754" }}
      >
        <Icon.PencilSquare size={20} />
      </Button>
      {rowData.isActive && (
        <Button
          variant="link"
          className="p-0"
          onClick={() => handleDelete(rowData)}
          title="Delete"
          style={{ color: "#dc3545" }}
        >
          <Icon.Trash size={20} />
        </Button>
      )}
    </div>
  );

  return (
    <div className="content">
      <PageHeader
        title="Complementary List"
        beforeTitle={
          <PrimeButton
            icon="pi pi-refresh"
            rounded
            text
            severity="secondary"
            onClick={handleRefresh}
            tooltip="Refresh"
            tooltipOptions={{ position: "bottom" }}
            aria-label="Refresh Complementary"
          />
        }
      />
      <div className="content-body">
        <div className="container mt-4">
          <div className="row shadow">
            <div className="col-md-12 mb-3">
              <div className="d-flex align-items-center gap-2 my-3">
                <IconField iconPosition="left" className="flex-grow-1">
                  <InputIcon>
                    <Icon.Search className="tx-12" />
                  </InputIcon>
                  <InputText
                    value={globalFilter}
                    onChange={(e) => {
                      setGlobalFilter(e.target.value);
                      setPage(1);
                      localStorage.setItem("complementaryTablePage", "1");
                    }}
                    placeholder="Search"
                    style={{ minWidth: 250 }}
                  />
                </IconField>
                <Button
                  className="btn btn-primary ms-2"
                  style={{ minWidth: 160 }}
                  onClick={() => {
                    setComplementary(initialComplementary);
                    setShowCreateForm(true);
                  }}
                >
                  <Icon.PlusCircle className="me-1" /> Create Complementary
                </Button>
              </div>

              <DataTable
                value={complementaries}
                dataKey="id"
                paginator
                first={(page - 1) * rows}
                rows={rows}
                totalRecords={totalRecords}
                onPage={(e) => {
                  const newPage = (e.page ?? 0) + 1;
                  const newRows = e.rows;
                  setPage(newPage);
                  setRows(newRows);
                  localStorage.setItem(
                    "complementaryTablePage",
                    newPage.toString()
                  );
                  localStorage.setItem(
                    "complementaryTableRows",
                    newRows.toString()
                  );
                }}
                onSort={(e) => {
                  const newSortField = e.sortField || "name";
                  const newSortOrder = e.sortOrder as 1 | -1 | 0;
                  setSortField(newSortField);
                  setSortOrder(newSortOrder);
                  localStorage.setItem(
                    "complementaryTableSortField",
                    newSortField
                  );
                  localStorage.setItem(
                    "complementaryTableSortOrder",
                    newSortOrder.toString()
                  );
                }}
                sortField={sortField}
                sortOrder={sortOrder}
                lazy
                loading={listLoading}
                rowsPerPageOptions={[5, 10, 20]}
                stripedRows
                sortMode="single"
                emptyMessage="No complementaries found."
                className="grid-table"
              >
                {/* <Column field="id" header="Id" sortable /> */}
                <Column field="name" header="Name" sortable />
                <Column field="description" header="Description" sortable />
                <Column
                  header="Actions"
                  body={actionBodyTemplate}
                  style={{ minWidth: "120px" }}
                />
              </DataTable>

              {showCreateForm && (
                <div
                  className="modal show d-block"
                  tabIndex={-1}
                  style={{ background: "rgba(0,0,0,0.3)" }}
                >
                  <div className="modal-dialog">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">
                          {complementary.id
                            ? "Update Complementary"
                            : "Create Complementary"}
                        </h5>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowCreateForm(false)}
                        ></button>
                      </div>
                      <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                          <div className="mb-3">
                            <label className="form-label">Name:</label>
                            <input
                              className="form-control"
                              name="name"
                              value={complementary.name || ""}
                              onChange={handleChange}
                              required
                              disabled={!!complementary.id}
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Description:</label>
                            <textarea
                              className="form-control"
                              name="description"
                              value={complementary.description || ""}
                              onChange={handleChange}
                            />
                          </div>
                          {error && (
                            <div className="alert alert-danger">{error}</div>
                          )}
                        </div>
                        <div className="modal-footer">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                          >
                            {loading
                              ? complementary.id
                                ? "Updating..."
                                : "Creating..."
                              : complementary.id
                              ? "Update"
                              : "Create"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setShowCreateForm(false)}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplementaryComponent;
export { ComplementaryComponent };
